import { DropzoneFile } from 'components/Dropzone/utils';
import { Phone } from 'phoneNumberUtils';
import { ObjectValues } from 'types';
import { EnterpriseMember } from './Enterprises';

export const USER_TYPE = {
  SUPER_ADMIN: 'Super admin',
  ADMIN: 'Admin',
  USER: 'User',
} as const;

export type Role = ObjectValues<typeof USER_TYPE>;

export const USER_SEARCH_PREFERENCE = {
  ANY: 'any',
  SPECIFIC: 'specific',
} as const;

type UserSearchPreferenceMode = ObjectValues<typeof USER_SEARCH_PREFERENCE>;

type AccredititationDetails = 'CA' | 'CPA' | 'IPAA';

type AccreditedInvestor = 'Yes' | 'No' | 'Unsure';

type ClientDetails = {
  firstName: string;
  lastName: string;
  email: string;
  mobilePhoneNumber: string;
  location: string;
  companyEmployer: string;
};

type AccountantDetails = {
  firstName: string;
  lastName: string;
  firm: string;
  email: string;
  phone: string;
  city: string;
  confirmKnowsClient: boolean;
};

type CompanyDetails = {
  ACN: string;
  name: string;
};

export type Accountant = {
  _id: string;
  briefNote: string;
  formStatus: 'not_sent' | 'pending' | 'completed';
  sentDate: Date;
  completedDate: Date;
  clientDetails: Partial<ClientDetails>;
  accountantDetails: Partial<AccountantDetails>;
  accreditationDetails: AccredititationDetails[];
  membershipNumber: string;
  accreditedInvestor: AccreditedInvestor;
  directorOfCompanies: CompanyDetails[];
  createAccountOnResolve: boolean;
};

export const PREFERRED_REVENUE_MODELS = {
  RECURRING: 'recurring',
  CONTRACT: 'contract',
  COMMISSION: 'commission',
  AD_HOC_SALES: 'ad_hoc_sales',
} as const;

export type PreferredRevenueModels = ObjectValues<
  typeof PREFERRED_REVENUE_MODELS
>;

export const MANAGEMENT_TEAM = {
  UNDER_MANAGEMENT: 'under_management',
  KEY_ROLE: 'key_role',
  SUCCESSION_PLAN: 'succession plan',
} as const;

export type ManagementTeam = ObjectValues<typeof MANAGEMENT_TEAM>;

export const GM_IN_PLACE = {
  PREFERRED: 'preferred',
  REQUIRED: 'required',
} as const;

export type GmInPlace = ObjectValues<typeof GM_IN_PLACE>;

export const ACQUISITION_TIMELINE = {
  ASAP: 'asap',
  WITHIN_ONE_YEAR: 'within_one_year',
  ONE_TO_THREE_YEARS: 'one_to_three_years',
  THREE_TO_FIVE_YEARS: 'three_to_five_years',
  NA: 'na',
} as const;

export type AcquistionTimeline = ObjectValues<typeof ACQUISITION_TIMELINE>;

export const POLL_NOTIFICATION_INTENSITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
} as const;

export type PollNotificationIntensity = ObjectValues<
  typeof POLL_NOTIFICATION_INTENSITY
>;

export const VIEW_SELECT = {
  TABLE: 'isGridViewForDeals',
  GRID: 'isGridViewForLeads',
} as const;

export type ViewSelectProp = ObjectValues<typeof VIEW_SELECT>;

export type UserViews = {
  [VIEW_SELECT.TABLE]?: boolean;
  [VIEW_SELECT.GRID]?: boolean;
};

export const HIDE_TABLE_COLUMNS = {
  BUYER: 'hideBuyerPortfolioColumns',
  SELLER: 'hideSellerPortfolioColumns',
} as const;

export const SHOW_TABLE_COLUMNS = {
  LEAD: 'leadTableColumns',
} as const;

export type ManageTableColumns =
  | ObjectValues<typeof HIDE_TABLE_COLUMNS>
  | ObjectValues<typeof SHOW_TABLE_COLUMNS>;

export type TableSettings = {
  [HIDE_TABLE_COLUMNS.BUYER]: string[];
  [HIDE_TABLE_COLUMNS.SELLER]: string[];
};

export type User = {
  _id: string;

  socialLogin?: boolean;

  email?: string;
  hasVerifiedEmail?: boolean;

  phone?: Phone;
  contactNumber?: string;
  hasVerifiedContactNumber?: boolean;

  role: Role;

  firstName: string;
  lastName: string;

  title?: string;

  linkedinAccount: string;
  hasVerifiedLinkedinAccount: boolean;

  profileImage: DropzoneFile;

  totalUnreadMessages: number;

  lastSeen?: Date;

  accountant?: Partial<Accountant>;

  enterpriseMember?: EnterpriseMember;

  snoozedDealRoomBannerNotification?: boolean;

  leadTableColumns: string[];

  // company section in profile
  isBusinessBroker?: boolean;
  companyName?: string;
  companyABN?: string;
  firmLogo?: DropzoneFile;

  // for portfolio
  hasBuyingDeals?: boolean;
  hasSellingDeals?: boolean;
  hasSellingUrgentDeals?: boolean;

  // in LCW
  canSkipPayment?: boolean;

  // Profile tab
  // Profile tab - Acquistion preferences
  hasAcquisitionPreferences: boolean;

  // Profile tab - Location
  preferredLocationMode: UserSearchPreferenceMode;
  preferredLocations: Array<string>;

  preferredBudgetMode: UserSearchPreferenceMode;
  budgetMinPrice: number;
  budgetMaxPrice: number;

  preferredIndustryIncludeMode: UserSearchPreferenceMode;
  preferredIncludeIndustries: Array<string>;

  preferredIndustryExcludeMode: UserSearchPreferenceMode;
  preferredExcludeIndustries: Array<string>;

  // Profile tab - Financials
  ebitda: number;
  revenueMin: number;
  ebitdaMin: number;
  ebitdaMax: number;
  preferredRevenueModels: PreferredRevenueModels[];

  // Profile tab - Advanced preferences
  managementTeam: ManagementTeam;
  gmInPlace: GmInPlace;
  acquistionTimeline: AcquistionTimeline;

  interestedInMoreBusinesses: boolean;
  interestedInCommercialProperty: boolean;

  // Settings tab
  hasActiveDeleteRequestAccount?: boolean;
  isSubscribedToNewsletter?: boolean;

  // Notification tab
  pollNotificationIntensity: PollNotificationIntensity | null;
  enablePollEmailNotifications: boolean;
  enablePollSmsNotifications: boolean;

  promotedAt?: Date;
  createdAt?: Date;

  hasRemindedToVerifyPhone?: boolean; // show Verify phone number if false

  fileReorderViewMode: FileReorderViewMode;

  avatarColor: AvatarColors;
} & TableSettings &
  UserViews &
  UserPreferences;

export type BasicUserInfo = Pick<
  User,
  | 'firstName'
  | 'lastName'
  | 'title'
  | 'profileImage'
  | 'linkedinAccount'
  | 'phone'
  | 'companyName'
>;

export type IdentityUserInfo = Pick<
  User,
  'firstName' | 'lastName' | 'email'
> & { colleagueId?: string };

export const FILE_REORDER_VIEW_MODE = {
  LEAD: 'lead',
  FILE: 'file',
} as const;

export type FileReorderViewMode = ObjectValues<typeof FILE_REORDER_VIEW_MODE>;

export type UserPreferences = {
  seenFileReorderFeature?: boolean;
  seenEnquiryContextFeature?: boolean;
  seenStarringFilesFeature?: boolean;
  seenDataRoomMessagingFeature?: boolean;
  seenAnalyticsExportFeature?: boolean;
  seenSecureLinkAnalyticsFeature?: boolean;

  doesNotShowDataRoomSellSideOnboarding?: boolean;
  doesNotShowDataRoomBuySideOnboarding?: boolean;
};

export const AVATAR_COLORS = [
  '#4F46E5',
  '#0EA5E9',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
] as const;

export type AvatarColors = typeof AVATAR_COLORS[number];
